import { computed } from "vue";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  type Maybe,
} from "../lib/maybe";
import { defineStore } from "pinia";
import { assertNever } from "../lib/assertNever";
import { createTimeoutHandle } from "../lib/timeoutHandle";
import { useTea } from "../lib/tea";
import {
  entityDisplayName,
  findOccurrencesInFile,
  type EntityOccurrence,
} from "../editor/entityOccurrences";
import { syncHarperLexicon } from "../editor/harperEngine";
import { collectDatabaseDisplayNames } from "../lib/projectDictionary";
import { isCloudReaderHosted } from "../lib/cloudEditorHost";
import type { EntityFrontmatter } from "../lib/entityFrontmatter";
import { sceneFilesInOrder } from "../lib/zwConfig";
import { getProjectDatabaseEntryFields } from "../lib/tauri/database";
import { joinProjectPath } from "../lib/runtime";
import { readProjectFile } from "../lib/tauri/core";
import { useActiveEntityFieldsStore } from "./useActiveEntityFieldsStore";
import { useEditorSessionStore } from "./useEditorSessionStore";
import { useShareModeStore } from "./useShareModeStore";
import {
  initialEditorHeadingsModel,
  updateEditorHeadings,
  type EditorHeadingsCmd,
  type EditorHeadingsModel,
  type EditorHeadingsMsg,
  type HarperLiveDatabase,
  type OccurrenceSessionFacts,
} from "./useEditorHeadingsStore.update";

type LiveDatabaseLexicon = {
  fileName: string;
  doc: string;
  fields?: EntityFrontmatter;
};

/**
 * Entity occurrence scans and Harper lexicon refresh for the open project.
 *
 * TEA: pure Model/Msg in `useEditorHeadingsStore.update.ts`. This shell
 * snapshots session facts into Msgs and runs the Cmds (file reads, Harper
 * sync, debounce timers). `entityOccurrences` stays writable (a write
 * dispatches `OccurrencesSet`).
 */
export const useEditorHeadingsStore = defineStore("editorHeadings", () => {
  const session = useEditorSessionStore();

  const harperLexiconTimer = createTimeoutHandle();
  const entityOccurrencesTimer = createTimeoutHandle();

  /** Share sessions cannot list Database entries; Harper and occurrence
   * scans both depend on those invokes. Skip entirely for /reader. */
  function shareSkipsDatabaseWork(): boolean {
    return useShareModeStore().shareMode || isCloudReaderHosted();
  }

  function occurrenceFacts(): OccurrenceSessionFacts {
    return {
      root: session.rootPath,
      hasProjectConfig: isJust(session.projectConfig),
      activeDatabase: session.activeDatabase,
      activeMap: session.activeMap,
    };
  }

  async function runCmd(cmd: EditorHeadingsCmd): Promise<void> {
    switch (cmd.kind) {
      case "fetchEntryFields": {
        const { root, dbName } = cmd.target;
        const entityName = entityDisplayName(session.doc, dbName);
        const liveFields = useActiveEntityFieldsStore().fieldsForEntry(
          root,
          dbName,
        );
        const { aka, plural, adjective } = await matchMaybe(liveFields, {
          just: async (fields) => fields,
          nothing: async () => getProjectDatabaseEntryFields(root, dbName),
        });
        await dispatch({
          type: "EntryFieldsFetched",
          requested: cmd.target,
          entry: { name: entityName, aka, plural, adjective },
          current: occurrenceFacts(),
          files: sceneFilesInOrder(session.manuscript),
        });
        return;
      }
      case "scanOccurrences": {
        const { root } = cmd;
        const entry = {
          name: cmd.entry.name,
          aka: [...cmd.entry.aka],
          plural: cmd.entry.plural,
          adjective: cmd.entry.adjective,
        };
        const batches = await Promise.all(
          cmd.files.map(async (file) => {
            try {
              const content = await readProjectFile(
                joinProjectPath(root, file),
              );
              return findOccurrencesInFile(file, content, entry);
            } catch {
              return [] as EntityOccurrence[];
            }
          }),
        );
        await dispatch({
          type: "OccurrencesScanned",
          occurrences: batches.flat(),
        });
        return;
      }
      case "syncHarperLexicon": {
        if (isNothing(cmd.root)) {
          await syncHarperLexicon({ rootPath: null });
          return;
        }
        const root = cmd.root.value;
        const live = matchMaybe(cmd.live, {
          nothing: (): Maybe<LiveDatabaseLexicon> => nothing,
          just: (l): Maybe<LiveDatabaseLexicon> =>
            just({
              fileName: l.fileName,
              doc: l.doc,
              ...(isJust(l.fields) ? { fields: l.fields.value } : {}),
            }),
        });
        const names = await collectDatabaseDisplayNames(root, live);
        await syncHarperLexicon({
          rootPath: root,
          databaseNames: names,
        });
        return;
      }
      case "scheduleHarperRefresh":
        harperLexiconTimer.schedule(350, () => {
          void refreshHarperLexicon();
        });
        return;
      case "scheduleOccurrencesRefresh":
        entityOccurrencesTimer.schedule(350, () => {
          void refreshEntityOccurrences();
        });
        return;
      case "clearTimers":
        harperLexiconTimer.clear();
        entityOccurrencesTimer.clear();
        return;
      default:
        assertNever(cmd);
    }
  }

  const { model, dispatch } = useTea<
    EditorHeadingsModel,
    EditorHeadingsMsg,
    EditorHeadingsCmd
  >(initialEditorHeadingsModel, updateEditorHeadings, {
    runCmd: just(runCmd),
  });

  const entityOccurrences = computed<EntityOccurrence[]>({
    get: () => model.value.entityOccurrences,
    set: (occurrences) => {
      void dispatch({ type: "OccurrencesSet", occurrences });
    },
  });

  async function refreshEntityOccurrences(): Promise<void> {
    await dispatch({
      type: "RefreshOccurrences",
      shareSkips: shareSkipsDatabaseWork(),
      facts: occurrenceFacts(),
    });
  }

  async function refreshHarperLexicon(): Promise<void> {
    const root = session.rootPath;
    const live = matchMaybe(root, {
      nothing: (): Maybe<HarperLiveDatabase> => nothing,
      just: (rootPath): Maybe<HarperLiveDatabase> =>
        matchMaybe(session.activeDatabase, {
          nothing: (): Maybe<HarperLiveDatabase> => nothing,
          just: (dbName): Maybe<HarperLiveDatabase> =>
            just({
              fileName: dbName,
              doc: session.doc,
              fields: useActiveEntityFieldsStore().fieldsForEntry(
                rootPath,
                dbName,
              ),
            }),
        }),
    });
    await dispatch({
      type: "RefreshHarperLexicon",
      shareSkips: shareSkipsDatabaseWork(),
      root,
      live,
    });
  }

  function scheduleHarperLexiconRefresh(): void {
    void dispatch({
      type: "ScheduleHarperRefresh",
      shareSkips: shareSkipsDatabaseWork(),
    });
  }

  function scheduleEntityOccurrencesRefresh(): void {
    void dispatch({ type: "ScheduleOccurrencesRefresh" });
  }

  function dispose(): void {
    void dispatch({ type: "Dispose" });
  }

  return {
    entityOccurrences,
    refreshEntityOccurrences,
    scheduleHarperLexiconRefresh,
    scheduleEntityOccurrencesRefresh,
    refreshHarperLexicon,
    dispose,
  };
});
